import { useMemo } from "react";
import { type ColumnDef } from "@tanstack/react-table";
import { Download } from "lucide-react";
import AdminPortalShell from "@/components/admin/AdminPortalShell";
import AdminTable from "@/components/admin/AdminTable";
import { AUDIT_EVENTS, type AuditEvent } from "@/components/admin/_mockData";

/**
 * /admin/audit — global audit log viewer. Backed by `AdminTable`
 * (TanStack Table) as of ROADS-478 phase 2. Sorting + global filter +
 * pagination inherited; column defs below own the cell rendering for
 * the operator avatar pair, the color-coded action pill, the multi-
 * line target/detail cells, and the per-row JSON button.
 *
 * Wires to `GET /api/admin/audit` in ROADS-476 deliverable D1.
 */
export default function AdminAuditLogPage() {
  const columns = useMemo<ColumnDef<AuditEvent, unknown>[]>(
    () => [
      {
        id: "timestamp",
        accessorKey: "timestamp",
        header: "Timestamp",
        cell: ({ row }) => (
          <div>
            <div className="font-mono text-xs font-semibold text-slate-900">
              {row.original.timestampLabel}
            </div>
            <div className="text-[11px] text-slate-500">
              {row.original.timestamp.slice(0, 10)} · {row.original.ago}
            </div>
          </div>
        ),
      },
      {
        id: "operator",
        accessorKey: "operatorName",
        header: "Operator",
        cell: ({ row }) => {
          const e = row.original;
          return (
            <div className="flex items-center gap-2">
              <div
                aria-hidden="true"
                className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold text-white ${
                  e.isSystem ? "bg-slate-500" : "bg-indigo-700"
                }`}
              >
                {e.isSystem ? "S" : "BC"}
              </div>
              <div className="min-w-0">
                <div className="truncate text-xs font-semibold text-slate-900">
                  {e.operatorName}
                </div>
                <div className="truncate text-[10px] text-slate-500">
                  {e.operatorEmail}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        id: "action",
        accessorKey: "action",
        header: "Action",
        cell: ({ row }) => {
          const e = row.original;
          const tones: Record<
            AuditEvent["actionKind"],
            { text: string; bg: string }
          > = {
            ok: { text: "text-emerald-700", bg: "bg-emerald-50" },
            warn: { text: "text-amber-700", bg: "bg-amber-50" },
            danger: { text: "text-red-700", bg: "bg-red-50" },
            info: { text: "text-indigo-700", bg: "bg-indigo-50" },
          };
          const t = tones[e.actionKind];
          return (
            <span
              className={`rounded-md px-2.5 py-1 font-mono text-[11px] font-semibold ${t.bg} ${t.text}`}
            >
              {e.action}
            </span>
          );
        },
      },
      {
        id: "target",
        accessorKey: "target",
        header: "Target",
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="truncate text-xs font-semibold text-slate-900">
              {row.original.target}
            </div>
            <div className="truncate text-[11px] text-slate-500">
              {row.original.targetSub}
            </div>
          </div>
        ),
      },
      {
        id: "detail",
        accessorKey: "detail",
        header: "Detail",
        cell: ({ row }) => {
          const e = row.original;
          const color =
            e.detailKind === "warn"
              ? "text-amber-700"
              : e.detailKind === "danger"
                ? "text-red-700"
                : "text-slate-700";
          return (
            <div className={`text-xs font-medium ${color}`}>{e.detail}</div>
          );
        },
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <button
              type="button"
              aria-label={`Show JSON for ${row.original.action}`}
              className="rounded-md border border-slate-200 px-2.5 py-1 font-mono text-[10px] font-semibold text-slate-500 hover:bg-slate-50"
              data-testid={`admin-audit-row-json-${row.original.id}`}
            >
              JSON
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <AdminPortalShell
      title="Audit log"
      subtitle="248 events · last 30d"
      testId="admin-audit-log-page"
      action={
        <button
          type="button"
          className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          <Download aria-hidden="true" className="h-3.5 w-3.5" />
          Export filtered
        </button>
      }
    >
      <LiveBar />
      <AdminTable
        data={AUDIT_EVENTS}
        columns={columns}
        enableGlobalFilter
        globalFilterPlaceholder="Search action codes, IDs, metadata…"
        pageSize={25}
        testId="admin-audit-log"
      />
    </AdminPortalShell>
  );
}

/**
 * "Live · auto-refresh" pill row above the table. Sits outside the
 * AdminTable's chrome because it's a status indicator, not a filter.
 * The future SSE-backed live mode (ROADS-476 D1) will toggle this pill
 * between "Live · auto-refresh" (connected) and "Paused" (operator
 * paused) / "Disconnected" (SSE drop).
 */
function LiveBar() {
  return (
    <div className="mb-4 flex items-center justify-end">
      <span className="flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 text-[11px] font-semibold text-emerald-700">
        <span
          aria-hidden="true"
          className="h-2 w-2 rounded-full bg-emerald-500"
        />
        Live · auto-refresh
      </span>
    </div>
  );
}
